import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import useFocusTrap from '../../hooks/useFocusTrap';

const SIDE_STYLES = {
  right: { panel: 'right-0 translate-x-0', off: 'translate-x-full', animate: 'animate-slide-in-right' },
  left: { panel: 'left-0 translate-x-0', off: '-translate-x-full', animate: 'animate-slide-in-left' },
};

const WIDTHS = {
  sm: 'w-72',
  md: 'w-80 sm:w-96',
  lg: 'w-80 sm:w-[28rem]',
};

// Off-canvas panel (filtros, sidebar mobile, notificações).
// Portal + backdrop clicável + Esc fecha.
const Drawer = ({
  open,
  onClose,
  side = 'right',
  size = 'md',
  title,
  children,
  footer,
  className = '',
}) => {
  const trapRef = useFocusTrap(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === 'Escape' && onClose?.();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  const sideCfg = SIDE_STYLES[side] || SIDE_STYLES.right;
  const width = WIDTHS[size] || WIDTHS.md;

  return createPortal(
    <div className="fixed inset-0 z-[55] flex" aria-hidden={!open}>
      <div
        className="absolute inset-0 bg-black/50 animate-fade-in"
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        ref={trapRef}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Painel lateral'}
        tabIndex={-1}
        className={`relative ml-auto h-full bg-white shadow-2xl flex flex-col focus:outline-none ${width} ${sideCfg.animate} ${
          side === 'left' ? 'mr-auto ml-0' : ''
        } ${className}`}
      >
        {(title || onClose) && (
          <header className="flex items-center justify-between p-4 border-b border-app-border">
            {title && <h2 className="text-lg font-semibold text-app-text">{title}</h2>}
            <button
              type="button"
              onClick={onClose}
              className="ml-auto p-1.5 rounded-md text-app-text-secondary hover:bg-app-surface-hover hover:text-app-text transition-colors"
              aria-label="Fechar"
            >
              <X size={20} />
            </button>
          </header>
        )}
        <div className="flex-1 overflow-y-auto">{children}</div>
        {footer && <footer className="p-4 border-t border-app-border">{footer}</footer>}
      </aside>
    </div>,
    document.body
  );
};

export default Drawer;
